import { sortByMultipleDates } from '@omnipresentgroup/design-system';
import {
  type CreateTimeOffAPIRequest,
  type CreateTimeOffRequest,
  type GetEmployeeTimeOffsAPIResponse,
  type GetTimeOffTypesAPIResponse,
  type GetTimeOffTypesResponse,
  type TimeOff,
  type TimeOffScope,
  TimeOffStatus,
  type TimeOffWithScopeAPI,
} from '@omnipresentgroup/types';

export const transformTimeOffsFromAPI = <T extends TimeOffScope>(
  timeOffs: GetEmployeeTimeOffsAPIResponse<T>,
): TimeOff<T>[] => {
  const entries = Object.entries(timeOffs);
  const result: TimeOff<T>[] = entries.flatMap(([key, _]) =>
    timeOffs[key as TimeOffStatus.scheduled | TimeOffStatus.taken].map(
      (item: TimeOffWithScopeAPI<T>) => ({
        id: item.id,
        leaveType: item.leaveType,
        employeeId: item.employeeId,
        companyId: item.companyId,
        employeeNote: item.employeeNote,
        startDate: item.startDate,
        endDate: item.endDate,
        createdAt: item.createdAt,
        createdBy: item.createdBy,
        updatedAt: item.updatedAt,
        updatedBy: item.updatedBy,
        deletedBy: item.deletedBy,
        deletedAt: item.deletedAt,
        isTaken: item.isTaken,
        firstDayAfternoonOnly: item.firstDayAfternoonOnly,
        lastDayMorningOnly: item.lastDayMorningOnly,
        employeeName: (item as TimeOffWithScopeAPI<'company'>).employeeName,
        status: item.deletedAt ? TimeOffStatus.deleted : (key as TimeOffStatus),
      }),
    ),
  );

  const sortedResult = sortByMultipleDates(result, ['startDate'], 'desc');

  return sortedResult as TimeOff<T>[];
};

export const transformCreateTimeOffRequestToAPI = (
  createTimeOffRequest: CreateTimeOffRequest,
): CreateTimeOffAPIRequest => {
  return {
    id: createTimeOffRequest.id,
    leaveType: createTimeOffRequest.leaveType,
    employeeId: createTimeOffRequest.employeeId,
    companyId: createTimeOffRequest.companyId,
    employeeNote: createTimeOffRequest.employeeNote,
    startDate: createTimeOffRequest.startDate,
    endDate: createTimeOffRequest.endDate,
    createdAt: createTimeOffRequest.createdAt,
    createdBy: createTimeOffRequest.createdBy,
    updatedAt: createTimeOffRequest.updatedAt,
    updatedBy: createTimeOffRequest.updatedBy,
    deletedBy: createTimeOffRequest.deletedBy,
    deletedAt: createTimeOffRequest.deletedAt,
    isTaken: createTimeOffRequest.isTaken,
    firstDayAfternoonOnly: createTimeOffRequest.firstDayAfternoonOnly,
    lastDayMorningOnly: createTimeOffRequest.lastDayMorningOnly,
    hasConfirmedWithManager: createTimeOffRequest.hasConfirmedWithManager,
  };
};

export const transformGetTimeOffTypesFromAPI = (
  timeOffTypes: GetTimeOffTypesAPIResponse,
): GetTimeOffTypesResponse =>
  timeOffTypes.map((type) => ({
    id: type.id,
    name: type.name,
    countryCode: type.country_code,
  }));
